import React from 'react';
import Component from '../components/Component';

/*
  The sign-in door for anything that is not a React page in this app.

  It exists for Bottom Trumps, which is a separate Vite app served statically from
  server/apps/bottom-trumps/. It cannot render LoginScreen itself -- Google Sign-In is configured
  here, in web/ -- so when its host needs an account it sends the browser to
  /login?next=/games/bottom-trumps/, and this brings them back afterwards.

  AuthWrapper does the actual work: it checks the session, shows LoginScreen if there is none, and
  renders this component only once there is one. So reaching render() means "already signed in", and
  the only job left is to leave.

  ONLY THE HOST EVER SEES THIS. A guest joining with a code must never be sent to Google, which is
  why the game's api.ts has no blanket 401 interceptor and the server flags which 401s are worth
  signing in for.
*/

/** Where to go when `next` is missing or refused. The homepage, which lists the games. */
const FALLBACK = '/';

/**
 * Validate `next` as a same-origin relative path.
 *
 * WITHOUT THIS THE ROUTE IS AN OPEN REDIRECT: /login?next=https://evil.example would send a signed-in
 * user straight there, with our domain in the link that got them to it.
 *
 * A leading single slash is the whole test, and the excluded cases are the ones that look like paths
 * and are not. `//evil.example` is protocol-relative and resolves off-site. `/\evil.example` is
 * treated the same way by some browsers, which is exactly the kind of near-miss a
 * `startsWith('/')` check on its own waves through.
 *
 * @param {string|null} raw - The `next` parameter as given
 * @returns {string} A safe path to navigate to
 */
export const safeNext = raw => {
  if (!raw || !raw.startsWith('/')) return FALLBACK;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return FALLBACK;
  return raw;
};

class Login extends Component {
  componentDidMount() {
    const next = safeNext(new URLSearchParams(window.location.search).get('next'));

    /*
      A full page load, not a react-router navigate. `next` points at a separately built app served
      as static files, which this router knows nothing about -- navigating client-side would render
      NoPage. `replace` rather than `assign` so Back does not land on this redirect and bounce
      forward again.
    */
    window.location.replace(next);
  }

  render() {
    // Shown for the instant before the browser leaves. Never the resting state of this page.
    return (
      <div className="auth-loading">
        <div className="loading-spinner" />
        <p>Signed in. Taking you back…</p>
      </div>
    );
  }
}

export default Login;
